/**
 * Pure helpers for showing a person in a staff list: avatar initials, a
 * relative "last seen", and the 30-day inactivity rule. Shared by the Team
 * table (`primitives/team-roster.tsx`) and HQ's user pages.
 */

/** Days without a sign-in before someone counts as inactive. */
export const STALE_AFTER_DAYS = 30

/** "Today", "Yesterday", "3 days ago", "2 weeks ago", "4 months ago"; null = "Never". */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) {
    return 'Never'
  }
  const then = new Date(iso)
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const days = Math.round((startOfDay(now) - startOfDay(then)) / 86_400_000)
  if (days <= 0) {
    return 'Today'
  }
  if (days === 1) {
    return 'Yesterday'
  }
  if (days < 14) {
    return `${days} days ago`
  }
  if (days < 60) {
    return `${Math.floor(days / 7)} weeks ago`
  }
  if (days < 365) {
    return `${Math.floor(days / 30)} months ago`
  }
  const years = Math.floor(days / 365)
  return years === 1 ? '1 year ago' : `${years} years ago`
}

/** First and last word of the name, else the first two letters of the email's local part. */
export function initials(name: string | null | undefined, email: string): string {
  const source = (name ?? '').trim() || email.split('@')[0] || email
  const parts = source.split(/\s+/).filter(Boolean)
  const letters = parts.length > 1 ? `${parts[0]![0]}${parts[parts.length - 1]![0]}` : source.slice(0, 2)
  return letters.toUpperCase()
}

/** Signed in before, but not in the last 30 days. Never-signed-in is not stale. */
export function isStale(lastLoginAt: string | null | undefined, now: Date = new Date()): boolean {
  if (!lastLoginAt) {
    return false
  }
  return now.getTime() - new Date(lastLoginAt).getTime() > STALE_AFTER_DAYS * 86_400_000
}
